<app-title-bar title="Plate Lookup" description="Search a license plate across all companies.">
</app-title-bar>

<div class="pl-topbar">
  <form class="pl-search" (ngSubmit)="search()" role="search">
    <input
      class="form-control pl-search__plate"
      name="plate"
      type="search"
      placeholder="Search by license plate"
      aria-label="License plate"
      autocomplete="off"
      [(ngModel)]="plateInput"
    />
    <input
      class="form-control pl-search__state"
      name="state"
      type="text"
      placeholder="State"
      aria-label="State (optional)"
      maxlength="2"
      autocomplete="off"
      [(ngModel)]="stateInput"
    />
    <button type="submit" class="btn btn-primary btn-medium" [disabled]="loading">Search</button>
  </form>

  <div class="pl-drb" *ngIf="!loading && result && drbShops.length > 0">
    <span class="pl-drb__label">DRB</span>
    <select class="form-control" [(ngModel)]="drbLocationId" aria-label="DRB location">
      <optgroup *ngFor="let shop of drbShops" [label]="shop.shopName">
        <option *ngFor="let location of shop.locations" [value]="location.id">
          {{ shop.shopName }} · {{ location.name || 'Location' }}
        </option>
      </optgroup>
    </select>
    <button type="button" class="btn btn-primary btn-medium" disabled title="Coming soon">
      Check with DRB
    </button>
    <span class="pl-drb__hint">{{ drbHint }}</span>
  </div>
</div>

<p class="pl-state pl-state--error" *ngIf="validationMessage">{{ validationMessage }}</p>
<p class="pl-state" *ngIf="loading">Looking up {{ plateInput }}…</p>
<p class="pl-state pl-state--error" *ngIf="!loading && errorMessage">{{ errorMessage }}</p>
<p class="pl-state" *ngIf="!loading && !errorMessage && !result && !validationMessage">
  Search by license plate to see its activity, subscriptions and purchases.
</p>

<ng-container *ngIf="!loading && result">
  <p class="pl-state" *ngIf="shops.length === 0 && result.timeline.rows.length === 0">
    No records found for {{ result.query.plate }}.
  </p>

  <!-- Shop chips, only when the plate is at more than one shop -->
  <div class="pl-shops" *ngIf="shops.length > 1" role="group" aria-label="Filter by company">
    <button
      type="button"
      class="pl-chip"
      [class.pl-chip--on]="selectedShopId === null"
      (click)="selectShop(null)"
    >
      All companies
    </button>
    <button
      type="button"
      class="pl-chip"
      *ngFor="let shop of shops"
      [class.pl-chip--on]="selectedShopId === shop.companyId"
      (click)="selectShop(shop.companyId)"
    >
      {{ shop.shopName || dash }}
    </button>
  </div>

  <!-- Status strip -->
  <section class="pl-card pl-strip" *ngIf="stripShop as shop">
    <div>
      <div class="pl-strip__k">Status</div>
      <div class="pl-strip__v">
        <span class="pl-pill" [ngClass]="'pl-pill--' + stripStatus.tone">{{
          stripStatus.label
        }}</span>
      </div>
      <div class="pl-strip__s">{{ shop.shopName || dash }}</div>
    </div>
    <div>
      <div class="pl-strip__k">Active Subscription</div>
      <ng-container *ngIf="shop.activeMembership as m; else noActive">
        <div class="pl-strip__v">{{ m.package_name || dash }}</div>
        <div class="pl-strip__s">
          {{ m.source_id || sourceLabel(m.source) }} · Renewal date
          {{ formatDate(m.current_period_end, m.timezone) }}
        </div>
      </ng-container>
      <ng-template #noActive
        ><div class="pl-strip__v">{{ dash }}</div></ng-template
      >
    </div>
    <div>
      <div class="pl-strip__k">Last washed date</div>
      <ng-container *ngIf="shop.lastWash as w; else noWash">
        <div class="pl-strip__v">{{ formatDate(w.at, w.timezone) }}</div>
        <div class="pl-strip__s">
          {{ w.locationName || dash }}<span *ngIf="w.laneName"> · {{ w.laneName }}</span>
        </div>
      </ng-container>
      <ng-template #noWash
        ><div class="pl-strip__v">{{ dash }}</div></ng-template
      >
    </div>
    <div>
      <div class="pl-strip__k">Washes redeemed</div>
      <div class="pl-strip__v">{{ shop.washesRedeemed }}</div>
      <div class="pl-strip__s">Rewash {{ shop.rewashes }}</div>
    </div>
    <div>
      <div class="pl-strip__k">Last sync</div>
      <ng-container *ngIf="shop.lastImport as imp; else noSync">
        <div class="pl-strip__v">{{ formatDate(imp.startedAt) }}</div>
        <div class="pl-strip__s">{{ imp.status | titlecase }}</div>
      </ng-container>
      <ng-template #noSync
        ><div class="pl-strip__v">{{ dash }}</div></ng-template
      >
    </div>
  </section>

  <!-- Alerts -->
  <div class="pl-alerts" *ngIf="alerts.length > 0">
    <div class="pl-alert" *ngFor="let alert of alerts" [ngClass]="'pl-alert--' + alert.tone">
      <strong>{{ alert.title }}</strong>
      <span>{{ alert.text }}</span>
    </div>
  </div>

  <div class="pl-grid">
    <!-- Main card: Activity first, then the other tabs -->
    <section class="pl-card pl-main">
      <div class="pl-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          class="pl-tab"
          [class.pl-tab--on]="activeTab === 'activity'"
          (click)="activeTab = 'activity'"
        >
          Activity <span class="pl-count">{{ activityCount(null) }}</span>
        </button>
        <button
          type="button"
          role="tab"
          class="pl-tab"
          [class.pl-tab--on]="activeTab === 'import'"
          (click)="activeTab = 'import'"
        >
          Import Status
        </button>
        <button
          type="button"
          role="tab"
          class="pl-tab"
          [class.pl-tab--on]="activeTab === 'membership'"
          (click)="activeTab = 'membership'"
        >
          Membership Status <span class="pl-count">{{ memberships.length }}</span>
        </button>
        <button
          type="button"
          role="tab"
          class="pl-tab"
          [class.pl-tab--on]="activeTab === 'purchases'"
          (click)="activeTab = 'purchases'"
        >
          Purchases <span class="pl-count">{{ purchases.length }}</span>
        </button>
      </div>

      <!-- ACTIVITY -->
      <div *ngIf="activeTab === 'activity'">
        <div class="pl-toolbar">
          <div class="pl-filters" role="group" aria-label="Filter activity">
            <button
              type="button"
              class="pl-chip"
              [class.pl-chip--on]="activityFilter === null"
              (click)="setActivityFilter(null)"
            >
              All
            </button>
            <button
              type="button"
              class="pl-chip"
              *ngFor="let f of activityFilters"
              [class.pl-chip--on]="activityFilter === f.key"
              (click)="setActivityFilter(f.key)"
            >
              {{ f.label }} <span class="pl-chip__n">{{ activityCount(f.key) }}</span>
            </button>
          </div>
          <label class="pl-toggle" *ngIf="otherVehicleCount > 0">
            <input type="checkbox" [checked]="showOtherVehicles" (change)="toggleOtherVehicles()" />
            Show other vehicles on these subscriptions ({{ otherVehicleCount }})
          </label>
        </div>

        <p class="pl-state pl-state--error" *ngIf="result.timeline.error">
          Some activity could not be loaded. What is shown is incomplete.
        </p>
        <p class="pl-empty" *ngIf="activityGroups.length === 0">No activity found.</p>

        <div class="pl-table-wrap" *ngIf="activityGroups.length > 0">
          <table class="pl-table pl-activity">
            <thead>
              <tr>
                <th scope="col">Date &amp; Time</th>
                <th scope="col">Event</th>
                <th scope="col">Details</th>
                <th scope="col">Location</th>
                <th scope="col">Lane</th>
              </tr>
            </thead>
            <tbody>
              <ng-container *ngFor="let group of activityGroups">
                <tr class="pl-month">
                  <td colspan="5">{{ group.month }}</td>
                </tr>
                <tr
                  *ngFor="let e of group.events; trackBy: trackEvent"
                  [class.pl-dim]="e.otherVehicle"
                >
                  <td class="pl-when">{{ formatDateTime(e.at, e.timezone) }}</td>
                  <td>
                    <span class="pl-ev" [ngClass]="'pl-ev--' + e.type">{{
                      activityLabel[e.type]
                    }}</span>
                  </td>
                  <td>
                    {{ activityDetail(e) }}
                    <span
                      class="pl-pill pl-pill--grey"
                      *ngIf="isImportActivity(e)"
                      [title]="importActivityTitle(e)"
                      >Import</span
                    >
                    <span class="pl-pill pl-pill--warn" *ngIf="e.vehicleMismatch">Mismatch</span>
                  </td>
                  <td>{{ e.locationName || dash }}</td>
                  <td>{{ e.laneName || dash }}</td>
                </tr>
              </ng-container>
            </tbody>
          </table>
        </div>
        <p class="pl-note">Gate opened / closed is recorded from CWS-1715.</p>
      </div>

      <!-- IMPORT STATUS -->
      <div *ngIf="activeTab === 'import'">
        <p class="pl-empty" *ngIf="importShops.length === 0">No roster sync for this plate.</p>
        <div class="pl-table-wrap" *ngIf="importShops.length > 0">
          <table class="pl-table">
            <thead>
              <tr>
                <th scope="col">Company</th>
                <th scope="col">Last sync</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let shop of importShops">
                <td>{{ shop.shopName || dash }}</td>
                <td>{{ shop.lastImport ? formatDateTime(shop.lastImport.startedAt) : dash }}</td>
                <td>{{ shop.lastImport ? (shop.lastImport.status | titlecase) : dash }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="pl-note">
          What each roster sync changed for this plate is recorded from CWS-1705.
        </p>
      </div>

      <!-- MEMBERSHIP STATUS -->
      <div *ngIf="activeTab === 'membership'">
        <p class="pl-state pl-state--error" *ngIf="result.sections.memberships.error">
          Subscriptions could not be loaded.
        </p>
        <p class="pl-empty" *ngIf="memberships.length === 0">No subscriptions found.</p>
        <div class="pl-table-wrap" *ngIf="memberships.length > 0">
          <table class="pl-table">
            <thead>
              <tr>
                <th scope="col">Member ID</th>
                <th scope="col">Plan</th>
                <th scope="col">Status</th>
                <th scope="col">Start date</th>
                <th scope="col">Renewal date / Ended</th>
                <th scope="col">Source</th>
                <th scope="col">Company</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let m of memberships">
                <td>{{ m.source_id || dash }}</td>
                <td>{{ m.package_name || dash }}</td>
                <td>
                  <span class="pl-pill" [ngClass]="'pl-pill--' + membershipTone(m)">{{
                    membershipStatusLabel(m)
                  }}</span>
                </td>
                <td>{{ formatDate(m.created_at, m.timezone) }}</td>
                <td>
                  <ng-container *ngIf="m.canceled_at; else renews"
                    >Ended {{ formatDate(m.canceled_at, m.timezone) }}</ng-container
                  >
                  <ng-template #renews>{{
                    formatDate(m.current_period_end, m.timezone)
                  }}</ng-template>
                </td>
                <td>{{ sourceLabel(m.source) }}</td>
                <td>{{ m.shop_name || dash }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- PURCHASES -->
      <div *ngIf="activeTab === 'purchases'">
        <p class="pl-state pl-state--error" *ngIf="result.purchases.error">
          Some purchases could not be loaded.
        </p>
        <p class="pl-empty" *ngIf="purchases.length === 0">No purchases found.</p>
        <div class="pl-table-wrap" *ngIf="purchases.length > 0">
          <table class="pl-table">
            <thead>
              <tr>
                <th scope="col">Date &amp; Time</th>
                <th scope="col">Type</th>
                <th scope="col">Plan</th>
                <th scope="col">Source</th>
                <th scope="col">Status</th>
                <th scope="col">Amount</th>
                <th scope="col">Location</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let p of purchases">
                <td class="pl-when">{{ formatDateTime(p.at, p.timezone) }}</td>
                <td>{{ purchaseKindLabel(p.kind) }}</td>
                <td>{{ p.planName || dash }}</td>
                <td>{{ p.source || dash }}</td>
                <td>{{ p.paymentStatus ? (p.paymentStatus | titlecase) : dash }}</td>
                <td>{{ money(p.amount) }}</td>
                <td>{{ p.locationName || dash }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Side card: Customer Info and Subscriptions -->
    <aside class="pl-side">
      <section class="pl-card">
        <h2 class="pl-card__title">Customer Info</h2>
        <p class="pl-empty" *ngIf="customers.length === 0">No customer holds this plate.</p>
        <article
          class="pl-person"
          *ngFor="let c of customers"
          [class.pl-person--inactive]="isInactiveCustomer(c)"
        >
          <header class="pl-person__head">
            <span class="pl-person__who">
              <span class="pl-person__name">{{ customerName(c) }}</span>
              <span class="pl-pill pl-pill--grey" *ngIf="isInactiveCustomer(c)">Inactive</span>
            </span>
            <span class="pl-person__shop">{{ c.shop_name || dash }}</span>
          </header>

          <dl class="pl-person__facts">
            <div>
              <dt>Phone number</dt>
              <dd>{{ c.contact || dash }}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{{ c.email || dash }}</dd>
            </div>
            <div>
              <dt>Member since</dt>
              <dd>{{ formatDate(c.created_at) }}</dd>
            </div>
          </dl>

          <div class="pl-person__cars" *ngIf="carsOf(c.id).length">
            <span class="pl-car" *ngFor="let car of carsOf(c.id)">
              <span class="pl-car__plate">{{ car.plate }}</span>
              <span class="pl-car__desc" *ngIf="car.description">{{ car.description }}</span>
            </span>
          </div>

          <ng-container
            *ngTemplateOutlet="planList; context: { $implicit: plansForSearchedCar(c.id) }"
          ></ng-container>
          <ng-container *ngIf="plansForOtherCars(c.id) as others">
            <ng-container *ngIf="others.length">
              <p class="pl-plan-group">Other plans on this customer</p>
              <ng-container
                *ngTemplateOutlet="planList; context: { $implicit: others }"
              ></ng-container>
            </ng-container>
          </ng-container>
          <ng-template #planList let-plans>
            <div class="pl-plan" *ngFor="let m of plans">
              <div class="pl-plan__top">
                <span class="pl-plan__name">
                  {{ m.package_name || dash }}
                  <span class="pl-plan__plate" *ngIf="planPlate(m) as plate">{{ plate }}</span>
                </span>
                <span class="pl-pill" [ngClass]="'pl-pill--' + membershipTone(m)">{{
                  membershipStatusLabel(m)
                }}</span>
              </div>
              <dl class="pl-plan__stats">
                <div>
                  <dt>{{ m.canceled_at ? 'Ended' : 'Renewal date' }}</dt>
                  <dd>{{ formatDate(m.canceled_at || m.current_period_end, m.timezone) }}</dd>
                </div>
                <div>
                  <dt>Washes redeemed</dt>
                  <dd>{{ washesOf(m.id).washes }}</dd>
                </div>
                <div>
                  <dt>Last washed date</dt>
                  <dd>{{ formatDate(washesOf(m.id).lastWashAt) }}</dd>
                </div>
              </dl>
            </div>
          </ng-template>
        </article>
      </section>

      <section class="pl-card">
        <details>
          <summary>Similar plates ({{ result.sections.lookAlikes.rows.length }})</summary>
          <p class="pl-empty" *ngIf="result.sections.lookAlikes.rows.length === 0">
            No similar plates.
          </p>
          <div class="pl-mono" *ngFor="let v of result.sections.lookAlikes.rows">
            {{ v.license_plate }} · {{ v.shop_name || dash }}
          </div>
        </details>
      </section>
      <section class="pl-card">
        <details>
          <summary>Plate edits ({{ result.sections.plateChanges.rows.length }})</summary>
          <p class="pl-empty" *ngIf="result.sections.plateChanges.rows.length === 0">
            No plate edits.
          </p>
          <div class="pl-edit" *ngFor="let p of result.sections.plateChanges.rows">
            {{ formatDateTime(p.created_at) }} · {{ p.previous_plate || dash }} →
            {{ p.new_plate }}
          </div>
        </details>
      </section>
    </aside>
  </div>
</ng-container>
